import React, { useMemo } from 'react';

/**
 * Wrapper for the legacy connectStateResults HOC from react-instantsearch-dom v6.
 * This provides the same API but uses the v7 react-instantsearch hooks internally.
 *
 * CRITICAL: This wrapper filters props to prevent infinite re-render loops.
 * Only valid InstantSearch data is passed to useInstantSearch, and component
 * props are kept separate to avoid triggering unnecessary hook re-executions.
 *
 * @param {Function} Component - A component that receives search state as props
 * @returns {React.Component} - A wrapped component connected to InstantSearch state
 */
export const connectStateResults = Component => {
  const ConnectedComponent = props => {
    // Lazy load the hook to avoid TransformStream issues in test environments
    let instantSearchData;
    try {
      // eslint-disable-next-line global-require
      const {
        useInstantSearch
      } = require('react-instantsearch');
      // eslint-disable-next-line react-hooks/rules-of-hooks
      instantSearchData = useInstantSearch();
    } catch (error) {
      // Fallback for test environment or when hook is not available
      instantSearchData = {
        results: null,
        scopedResults: [],
        indexUiState: {},
        status: 'idle'
      };
    }

    // Extract the data in the format expected by the v6 connector
    const {
      results,
      scopedResults,
      indexUiState,
      status
    } = instantSearchData;

    // Build searchState from indexUiState (v7 equivalent)
    const searchState = indexUiState || {};

    // Build searchResults from results (v7 equivalent)
    const searchResults = results;

    // Build allSearchResults from scopedResults - memoized to prevent re-renders
    const allSearchResults = useMemo(() => scopedResults?.reduce((acc, scopedResult) => {
      acc[scopedResult.indexId] = scopedResult.results;
      return acc;
    }, {}) || {}, [scopedResults]);

    // Determine if search is in progress
    const searching = status === 'loading' || status === 'stalled';

    // Pass all the props to the wrapped component
    // NOTE: Component props are passed through unchanged to avoid filtering issues
    return <Component {...props} searchState={searchState} searchResults={searchResults} allSearchResults={allSearchResults} searching={searching} />;
  };

  // Preserve display name for debugging
  ConnectedComponent.displayName = `connectStateResults(${Component.displayName || Component.name || 'Component'})`;
  return ConnectedComponent;
};
export default connectStateResults;